/*
 * Copyright (c) 2025 Sila Artificial Intelligence Research LLC
 * A DA Holding Ltd Company
 * All rights reserved.
 *
 * PROPRIETARY AND CONFIDENTIAL
 * This software and its documentation are proprietary to Sila Artificial
 * Intelligence Research LLC. Unauthorized copying, distribution, or use
 * of this software is strictly prohibited.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL
 * THE AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING
 * FROM, OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER
 * DEALINGS IN THE SOFTWARE.
 */

import { useState, useEffect } from 'react';
import axios from 'axios';
import { brandHealthServiceV2, isDemoMode } from '../services';
import { useDateFilter } from '../contexts/DateFilterContext';
import { getMonthlyBrandData, getMonthlyAllBrands } from '../services/monthlyMockData';

export interface TraditionalMetrics {
  awareness: number;
  consideration: number;
  usage: number;
  recent_usage: number;
  primary_brand: number;
  trust: number;
  pride: number;
  innovation: number;
  value: number;
  quality: number;
  taste?: number;
  texture?: number;
  natural?: number;
  nps: number;
  loyalty: number;
  advocacy: number;
  satisfaction?: number;
  conversion_rate?: number;
  retention_rate?: number;
  total_responses: number;
}

export interface DigitalMetrics {
  sentiment_score: number;
  engagement_rate: number;
  share_of_voice: number;
  mentions_count: number;
  positive_mentions: number;
  negative_mentions: number;
  neutral_mentions: number;
  trending_topics: string[];
  influencer_reach: number;
  viral_coefficient: number;
}

export interface BrandMetrics {
  brand_id: string;
  brand_name: string;
  traditional: TraditionalMetrics;
  digital: DigitalMetrics;
  combined_score: number;
  category: string;
  last_updated: Date;
}

/**
 * Hook to fetch real brand metrics from the API
 * Separates Traditional (survey) and Digital (social media) metrics
 * In demo mode, uses monthly mock data based on the selected date
 */
export function useBrandMetrics(brandId?: string) {
  const { selectedDate, getMonthFromDate } = useDateFilter();
  const [metrics, setMetrics] = useState<BrandMetrics | null>(null);
  const [allBrands, setAllBrands] = useState<any[]>([]);
  const [digitalMetrics, setDigitalMetrics] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Get the current month string for data lookup
  const currentMonth = getMonthFromDate(selectedDate);

  useEffect(() => {
    fetchBrands();
    if (brandId) {
      fetchBrandMetrics(brandId, currentMonth);
      fetchDigitalMetrics(brandId, currentMonth);
    }
  }, [brandId, currentMonth]); // Re-fetch when date changes

  const fetchBrands = async () => {
    try {
      if (isDemoMode) {
        // Use mock data in demo mode
        const brands = await brandHealthServiceV2.getBrands();
        setAllBrands(brands.map(b => ({
          brand_id: b.brand_id,
          name: b.name,
          is_digital: b.is_digital
        })));
      } else {
        // Use real API in production mode
        const response = await axios.get('/api/v2/metrics/brands');
        setAllBrands(response.data.brands || []);
      }
    } catch (err) {
      console.error('Failed to fetch brands:', err);
      setAllBrands([]);
    }
  };

  const fetchBrandMetrics = async (id: string, month: string) => {
    setLoading(true);
    setError(null);
    try {
      let data: any;

      if (isDemoMode) {
        // Use monthly mock data based on selected date
        const monthlyData = getMonthlyBrandData(month, id);

        if (monthlyData) {
          // Use the monthly data directly
          data = {
            brand_id: monthlyData.brand_id,
            name: monthlyData.name,
            category: 'Beverages',
            metrics: {
              awareness: monthlyData.metrics.awareness,
              consideration: monthlyData.metrics.consideration,
              usage: monthlyData.metrics.usage,
              recent_usage: monthlyData.metrics.recent_usage,
              primary_brand: monthlyData.metrics.primary_brand,
              trust: monthlyData.metrics.trust,
              pride: monthlyData.metrics.pride,
              innovation: monthlyData.metrics.innovation,
              value: monthlyData.metrics.value,
              quality: monthlyData.metrics.quality,
              taste: monthlyData.metrics.taste,
              texture: monthlyData.metrics.texture,
              natural: monthlyData.metrics.natural,
              nps: monthlyData.metrics.nps,
              loyalty: monthlyData.metrics.loyalty,
              advocacy: monthlyData.metrics.loyalty * 0.8, // Derived from loyalty
              satisfaction: monthlyData.metrics.quality * 0.95,
              overall: monthlyData.equity.total_equity / 100,
              total_count: monthlyData.respondent_count
            },
            respondent_count: monthlyData.respondent_count,
            digital: monthlyData.digital,
            equity: monthlyData.equity
          };
        } else {
          // Fallback to legacy mock data if monthly data not available
          const brand = await brandHealthServiceV2.getBrand(id);
          const equity = await brandHealthServiceV2.getLatestEquity();
          const brandEquity = equity.find(e => e.brand_id === id);
          const loyalty = await brandHealthServiceV2.getLoyalty(id);
          const brandLoyalty = loyalty[0];

          data = {
            brand_id: brand.brand_id,
            name: brand.name,
            category: 'Beverages',
            metrics: {
              awareness: (brandEquity?.total_equity || 75) / 100,
              consideration: ((brandEquity?.brand_index || 75) - 5) / 100,
              usage: 0.65,
              recent_usage: 0.55,
              primary_brand: 0.45,
              trust: (brandEquity?.trad_equity || 75) / 100,
              pride: 0.70,
              innovation: 0.60,
              value: 0.75,
              quality: 0.80,
              taste: 0.78,
              texture: 0.72,
              natural: 0.68,
              nps: brandLoyalty?.nps || 40,
              loyalty: (brandLoyalty?.avg_loyalty || 70) / 100,
              advocacy: 0.55,
              satisfaction: 0.75,
              overall: (brandEquity?.total_equity || 75) / 100,
              total_count: brandLoyalty?.total_users || 1250
            },
            respondent_count: brandLoyalty?.total_users || 1250
          };
        }
      } else {
        // Use real API in production mode
        const response = await axios.get(`/api/v2/metrics/brands/${id}`);
        data = response.data;
      }
      
      // Helper function to safely convert and cap percentages
      const toPercentage = (value: number | undefined, defaultValue: number = 0): number => {
        if (value === undefined || value === null) return defaultValue;
        const percentage = value * 100;
        return Math.min(100, Math.max(0, percentage)); // Cap between 0 and 100
      };
      
      // Transform API data into separated Traditional and Digital metrics
      const transformedMetrics: BrandMetrics = {
        brand_id: data.brand_id || data.id,
        brand_name: data.name || data.brand_name || 'Unknown Brand',
        category: data.category || 'Rice',
        traditional: {
          awareness: toPercentage(data.metrics?.awareness),
          consideration: toPercentage(data.metrics?.consideration),
          usage: toPercentage(data.metrics?.usage),
          recent_usage: toPercentage(data.metrics?.recent_usage),
          primary_brand: toPercentage(data.metrics?.primary_brand),
          trust: toPercentage(data.metrics?.trust),
          pride: toPercentage(data.metrics?.pride),
          innovation: toPercentage(data.metrics?.innovation),
          value: toPercentage(data.metrics?.value),
          quality: toPercentage(data.metrics?.quality),
          taste: data.metrics?.taste !== undefined ? toPercentage(data.metrics.taste) : undefined,
          texture: data.metrics?.texture !== undefined ? toPercentage(data.metrics.texture) : undefined,
          natural: data.metrics?.natural !== undefined ? toPercentage(data.metrics.natural) : undefined,
          nps: data.metrics?.nps || 0,
          loyalty: toPercentage(data.metrics?.loyalty),
          advocacy: toPercentage(data.metrics?.advocacy),
          satisfaction: data.metrics?.satisfaction !== undefined ? toPercentage(data.metrics.satisfaction) : undefined,
          conversion_rate: data.metrics?.conversion_rate !== undefined ? toPercentage(data.metrics.conversion_rate) : undefined,
          retention_rate: data.metrics?.retention_rate !== undefined ? toPercentage(data.metrics.retention_rate) : undefined,
          total_responses: data.respondent_count || data.metrics?.total_count || 0
        },
        digital: digitalMetrics || {
          // Fallback to calculated metrics if API call hasn't completed
          sentiment_score: calculateSentiment(data.metrics),
          engagement_rate: calculateEngagement(data.metrics),
          share_of_voice: calculateShareOfVoice(data.metrics),
          mentions_count: calculateMentions(data.metrics),
          positive_mentions: calculatePositiveMentions(data.metrics),
          negative_mentions: calculateNegativeMentions(data.metrics),
          neutral_mentions: calculateNeutralMentions(data.metrics),
          trending_topics: deriveTrendingTopics(data),
          influencer_reach: calculateInfluencerReach(data.metrics),
          viral_coefficient: calculateViralCoefficient(data.metrics)
        },
        combined_score: ((data.metrics?.overall || 0) * 100),
        last_updated: new Date()
      };
      
      setMetrics(transformedMetrics);
    } catch (err: any) {
      setError(err.message || 'Failed to fetch brand metrics');
      console.error('Failed to fetch brand metrics:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchDigitalMetrics = async (id: string, month: string) => {
    try {
      let digitalData: any;

      if (isDemoMode) {
        // Use monthly mock data based on selected date
        const monthlyData = getMonthlyBrandData(month, id);

        if (monthlyData) {
          // Use the monthly digital data
          const totalMentions = monthlyData.digital.mentions_count;
          const positivePct = monthlyData.digital.sentiment_score;
          const engagementRate = monthlyData.digital.engagement_rate;
          const shareOfVoice = monthlyData.digital.share_of_voice;

          // Enhanced digital metrics structure for tabs (Social Media, Content, SEO, Paid Media)
          digitalData = {
            // Basic metrics (for fallback)
            sentiment_score: positivePct,
            engagement_rate: engagementRate,
            share_of_voice: shareOfVoice,
            mentions_count: totalMentions,
            positive_mentions: Math.round(totalMentions * (positivePct / 100)),
            negative_mentions: Math.round(totalMentions * ((100 - positivePct) * 0.3 / 100)),
            neutral_mentions: Math.round(totalMentions * ((100 - positivePct) * 0.7 / 100)),
            trending_topics: ['refreshing', 'quality', 'trusted', 'classic'],
            influencer_reach: Math.round(totalMentions * 1.2),
            viral_coefficient: Math.round((engagementRate / 4) * 100) / 100,

            // Enhanced social media structure (enables tabs)
            social_media: {
              total_followers: Math.round(totalMentions * 15),
              avg_engagement_rate: Math.round(engagementRate * 100) / 100,
              community_health: Math.round(positivePct * 100) / 100,
              platforms: {
                Instagram: {
                  followers: Math.round(totalMentions * 8),
                  engagement_rate: Math.round((engagementRate * 1.2) * 100) / 100,
                  growth_rate: Math.round((2.5 + Math.random() * 2) * 100) / 100,
                  sentiment_score: Math.round((positivePct + 3) * 100) / 100
                },
                Twitter: {
                  followers: Math.round(totalMentions * 4),
                  engagement_rate: Math.round((engagementRate * 0.8) * 100) / 100,
                  growth_rate: Math.round((1.8 + Math.random() * 1.5) * 100) / 100,
                  sentiment_score: Math.round((positivePct - 2) * 100) / 100
                },
                Facebook: {
                  followers: Math.round(totalMentions * 6),
                  engagement_rate: Math.round((engagementRate * 0.6) * 100) / 100,
                  growth_rate: Math.round((1.2 + Math.random() * 1) * 100) / 100,
                  sentiment_score: Math.round(positivePct * 100) / 100
                },
                LinkedIn: {
                  followers: Math.round(totalMentions * 2),
                  engagement_rate: Math.round((engagementRate * 0.9) * 100) / 100,
                  growth_rate: Math.round((3.0 + Math.random() * 2) * 100) / 100,
                  sentiment_score: Math.round((positivePct + 5) * 100) / 100
                },
                TikTok: {
                  followers: Math.round(totalMentions * 3),
                  engagement_rate: Math.round((engagementRate * 2.5) * 100) / 100,
                  growth_rate: Math.round((8.0 + Math.random() * 5) * 100) / 100,
                  sentiment_score: Math.round((positivePct + 2) * 100) / 100
                }
              }
            },

            // Content performance metrics
            content_performance: {
              content_types: {
                video: {
                  avg_views: Math.round(totalMentions * 0.8),
                  avg_likes: Math.round(totalMentions * 0.15),
                  avg_comments: Math.round(totalMentions * 0.03),
                  avg_shares: Math.round(totalMentions * 0.05),
                  avg_engagement_rate: Math.round((engagementRate * 1.5) * 100) / 100
                },
                post: {
                  avg_views: Math.round(totalMentions * 0.5),
                  avg_likes: Math.round(totalMentions * 0.1),
                  avg_comments: Math.round(totalMentions * 0.02),
                  avg_shares: Math.round(totalMentions * 0.03),
                  avg_engagement_rate: Math.round((engagementRate * 1.2) * 100) / 100
                }
              },
              content_roi: Math.round((3.2 + Math.random() * 2) * 100) / 100,
              content_velocity: 12 + Math.round(Math.random() * 8),
              content_effectiveness: 68 + Math.round(Math.random() * 15),
              evergreen_ratio: Math.round((0.35 + Math.random() * 0.2) * 100) / 100
            },

            // Search Trends metrics (Google Trends style)
            search_trends: {
              search_interest_score: 65 + Math.round(Math.random() * 30),
              last_month_score: 60 + Math.round(Math.random() * 25),
              brand_awareness_index: 70 + Math.round(Math.random() * 25),
              competitor_gap: Math.round((Math.random() * 30 - 10) * 100) / 100,
              regional_coverage: 60 + Math.round(Math.random() * 35),
              trend_momentum: 55 + Math.round(Math.random() * 40),
              trend_direction: Math.random() > 0.3 ? 'up' : 'down',
              trend_change: Math.round((Math.random() * 15 + 2) * 100) / 100,
              peak_month: ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'][Math.floor(Math.random() * 12)],
              seasonal_index: 70 + Math.round(Math.random() * 25),
              current_season: ['High', 'Medium', 'Low'][Math.floor(Math.random() * 3)],
              related_queries: [
                'best beverage brand',
                'soft drink prices',
                'premium drinks',
                'refreshing beverages',
                'drink recipes',
                'healthy drink options',
                'carbonated drinks'
              ],
              geographic_interest: [
                { name: 'Riyadh', interest: 85 + Math.round(Math.random() * 10) },
                { name: 'Jeddah', interest: 75 + Math.round(Math.random() * 15) },
                { name: 'Dammam', interest: 60 + Math.round(Math.random() * 20) },
                { name: 'Makkah', interest: 55 + Math.round(Math.random() * 25) }
              ],
              competitor_comparison: [
                { name: 'Your Brand', interest: 80 + Math.round(Math.random() * 15) },
                { name: 'Competitor A', interest: 60 + Math.round(Math.random() * 20) },
                { name: 'Competitor B', interest: 50 + Math.round(Math.random() * 25) },
                { name: 'Competitor C', interest: 40 + Math.round(Math.random() * 20) }
              ],
              // Search trends over time for all brands
              trends_over_time: {
                labels: ['Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
                brands: [
                  {
                    name: 'Coca-Cola',
                    color: '#E61D2B',
                    data: [85, 82, 88, 92, 78, 95, 89]
                  },
                  {
                    name: 'Pepsi',
                    color: '#004B93',
                    data: [72, 78, 75, 82, 85, 80, 83]
                  },
                  {
                    name: 'Sprite',
                    color: '#008B47',
                    data: [45, 52, 68, 55, 48, 50, 52]
                  },
                  {
                    name: 'Fanta',
                    color: '#FF8300',
                    data: [38, 42, 55, 48, 40, 45, 43]
                  },
                  {
                    name: 'Mountain Dew',
                    color: '#A4D65E',
                    data: [32, 35, 42, 38, 45, 40, 38]
                  }
                ]
              }
            },

            // Paid media metrics
            paid_media: {
              channels: {
                google_ads: {
                  cpc: Math.round((0.85 + Math.random() * 0.5) * 100) / 100,
                  ctr: Math.round((3.2 + Math.random() * 1.5) * 100) / 100,
                  roas: Math.round((3.5 + Math.random() * 2) * 100) / 100,
                  quality_score: 7 + Math.round(Math.random() * 2)
                },
                meta_ads: {
                  cpm: Math.round((8.5 + Math.random() * 4) * 100) / 100,
                  ctr: Math.round((1.8 + Math.random() * 1) * 100) / 100,
                  roas: Math.round((2.8 + Math.random() * 1.5) * 100) / 100,
                  frequency: Math.round((2.1 + Math.random() * 0.8) * 100) / 100
                },
                programmatic: {
                  cpm: Math.round((4.2 + Math.random() * 2) * 100) / 100,
                  viewability: 72 + Math.round(Math.random() * 15),
                  roas: Math.round((2.2 + Math.random() * 1) * 100) / 100,
                  brand_safety: 95 + Math.round(Math.random() * 4)
                }
              },
              overall_roas: Math.round((2.8 + Math.random() * 1.5) * 100) / 100,
              cost_per_acquisition: 25 + Math.round(Math.random() * 15),
              budget_utilization: 85 + Math.round(Math.random() * 12),
              attribution_model: 'data-driven'
            },

            // Competitor analysis
            competitor_analysis: {
              share_of_voice: Math.round(shareOfVoice * 100) / 100,
              sentiment_comparison: Math.round((positivePct - 5) * 100) / 100,
              engagement_comparison: Math.round((engagementRate * 0.9) * 100) / 100
            },

            // Trend analysis
            trend_analysis: {
              viral_potential: 65 + Math.round(Math.random() * 25),
              trending_score: 72 + Math.round(Math.random() * 18)
            },

            // AI-generated insights
            insights: [
              `Strong engagement on Instagram with ${(Math.round((engagementRate * 1.2) * 100) / 100).toFixed(2)}% rate, outperforming industry average.`,
              `Video content drives 50% higher engagement than static posts.`,
              `SEO improvements have led to 23% increase in organic traffic this month.`,
              `TikTok shows highest growth potential with ${(Math.round((8.0 + Math.random() * 5) * 100) / 100).toFixed(2)}% follower growth.`,
              `Paid media ROAS of ${(Math.round((2.8 + Math.random() * 1.5) * 100) / 100).toFixed(2)}x indicates efficient ad spend.`
            ]
          };
        } else {
          // Fallback to legacy mock data
          const sentiment = await brandHealthServiceV2.getSentiment(id);
          const brandSentiment = sentiment[id] || {};
          const totalSentiment =
            (brandSentiment.positive?.count || 0) +
            (brandSentiment.neutral?.count || 0) +
            (brandSentiment.negative?.count || 0);

          const positivePct = totalSentiment > 0
            ? ((brandSentiment.positive?.count || 0) / totalSentiment * 100)
            : 70;

          digitalData = {
            sentiment_score: positivePct,
            engagement_rate: 4.5,
            share_of_voice: 25,
            mentions_count: totalSentiment,
            positive_mentions: brandSentiment.positive?.count || 0,
            negative_mentions: brandSentiment.negative?.count || 0,
            neutral_mentions: brandSentiment.neutral?.count || 0,
            trending_topics: ['refreshing', 'quality', 'trusted', 'classic'],
            influencer_reach: 50000,
            viral_coefficient: 1.3
          };
        }
      } else {
        // Use real API in production mode
        const response = await axios.get(`/api/v2/metrics/brands/${id}/digital`);
        digitalData = response.data?.digital_metrics;
      }

      if (digitalData) {
        setDigitalMetrics(digitalData);

        // Update metrics with real digital data if already loaded
        setMetrics(prev => {
          if (prev) {
            return {
              ...prev,
              digital: digitalData
            };
          }
          return prev;
        });
      }
    } catch (err) {
      console.error('Failed to fetch digital metrics:', err);
      // Don't set error as we have fallback calculated metrics
    }
  };

  const compareBrands = async (brandIds: string[]) => {
    try {
      const promises = brandIds.map(id => axios.get(`/api/v2/metrics/brands/${id}`));
      const responses = await Promise.all(promises);
      return responses.map(r => r.data);
    } catch (err) {
      console.error('Failed to compare brands:', err);
      return [];
    }
  };

  return {
    metrics,
    allBrands,
    digitalMetrics,
    loading,
    error,
    refetch: () => {
      if (brandId) {
        fetchBrandMetrics(brandId, currentMonth);
        fetchDigitalMetrics(brandId, currentMonth);
      }
    },
    compareBrands
  };
}

// Helper functions to calculate digital metrics from traditional data
function calculateSentiment(metrics: any): number {
  if (!metrics) return 70;
  // Base sentiment on NPS, trust, and quality
  const npsNormalized = (metrics.nps + 100) / 2; // Convert -100 to 100 range to 0-100
  const sentiment = (npsNormalized * 0.4 + (metrics.trust || 0.5) * 100 * 0.3 + (metrics.quality || 0.5) * 100 * 0.3);
  return Math.min(100, Math.max(0, sentiment));
}

function calculateEngagement(metrics: any): number {
  if (!metrics) return 3;
  // Base engagement on consideration and advocacy
  const engagement = ((metrics.consideration || 0.3) * 10 + (metrics.advocacy || 0.3) * 5) / 2;
  return Math.min(10, Math.max(1, engagement));
}

function calculateShareOfVoice(metrics: any): number {
  if (!metrics) return 20;
  // Base on awareness and primary brand
  return Math.min(50, (metrics.awareness || 0.5) * 40 + (metrics.primary_brand || 0.2) * 30);
}

function calculateMentions(metrics: any): number {
  if (!metrics) return 5000;
  // Base on total responses and awareness
  const baseMentions = (metrics.total_count || 1000) * 10;
  const awarenessMultiplier = (metrics.awareness || 0.5) * 2;
  return Math.floor(baseMentions * awarenessMultiplier);
}

function calculatePositiveMentions(metrics: any): number {
  if (!metrics) return 65;
  // Base on NPS and trust
  const npsPositive = metrics.nps > 0 ? metrics.nps / 100 * 30 : 0;
  return Math.min(90, 50 + npsPositive + (metrics.trust || 0.5) * 20);
}

function calculateNegativeMentions(metrics: any): number {
  if (!metrics) return 10;
  // Inverse of NPS if negative
  const npsNegative = metrics.nps < 0 ? Math.abs(metrics.nps) / 100 * 20 : 5;
  return Math.min(30, npsNegative);
}

function calculateNeutralMentions(metrics: any): number {
  const positive = calculatePositiveMentions(metrics);
  const negative = calculateNegativeMentions(metrics);
  return 100 - positive - negative;
}

function deriveTrendingTopics(data: any): string[] {
  const topics = [];
  const metrics = data.metrics || {};
  
  // Add topics based on high-scoring attributes
  if (metrics.quality > 0.6) topics.push('quality');
  if (metrics.taste > 0.6) topics.push('taste');
  if (metrics.value > 0.55) topics.push('value');
  if (metrics.natural > 0.5) topics.push('natural');
  if (metrics.innovation > 0.5) topics.push('innovative');
  if (metrics.texture > 0.55) topics.push('texture');
  
  // Add category-specific topics
  if (data.category === 'Rice') {
    topics.push('grain', 'cooking');
  }
  
  // Ensure we have at least 3 topics
  if (topics.length < 3) {
    topics.push('authentic', 'trusted');
  }
  
  return topics.slice(0, 6);
}

function calculateInfluencerReach(metrics: any): number {
  if (!metrics) return 30000;
  // Base on awareness and advocacy
  const baseReach = 20000;
  const awarenessBoost = (metrics.awareness || 0.5) * 50000;
  const advocacyBoost = (metrics.advocacy || 0.3) * 30000;
  return Math.floor(baseReach + awarenessBoost + advocacyBoost);
}

function calculateViralCoefficient(metrics: any): number {
  if (!metrics) return 1.0;
  // Base on advocacy and NPS
  const advocacy = metrics.advocacy || 0.3;
  const npsBoost = metrics.nps > 50 ? 0.5 : metrics.nps > 0 ? 0.3 : 0.1;
  return Math.min(2.5, 0.8 + advocacy + npsBoost);
}

export default useBrandMetrics;
